import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { gymsService } from '../services/gyms';
import { cragsService } from '../services/crags';
import { competitionsService } from '../services/competitions';
import { devicesService } from '../services/devices';
import LocationQrPoster from '../components/qr-poster/LocationQrPoster';
import {
  PX_PER_MM,
  copiesPerSheet,
  posterLayout,
  type QrPosterFormat,
} from '../components/qr-poster/page-format';
import { QR_POSTER_THEMES, type QrPosterThemeId } from '../components/qr-poster/poster-theme';
import { printNode } from '../utils/print-node';
import { trackEvent } from '../utils/analytics';
import { cn } from '../lib/utils';

/** Which kind of place the poster is for — it decides the service and the URL. */
export type QrPosterKind = 'gym' | 'crag' | 'competition';

/** The segment the public page lives under, which is what the code encodes. */
const PROFILE_SEGMENT: Record<QrPosterKind, string> = {
  gym: 'gyms',
  crag: 'crags',
  competition: 'competitions',
};

const FORMAT_OPTIONS: QrPosterFormat[] = ['a6', 'a5', 'a4'];
const THEME_OPTIONS: QrPosterThemeId[] = ['lime', 'ink', 'mono'];

/** What the poster needs to know about the place, whatever kind it is. */
interface PosterLocation {
  name: string;
  subtitle?: string | null;
  logoUrl?: string | null;
}

/**
 * A page of the place the code can be pointed at. `search` is what the gym
 * profile's own tab strip writes into the URL, so a scan lands on that tab.
 */
interface PosterTarget {
  id: string;
  label: string;
  search?: string;
}

export default function QrPosterPage({ kind }: { kind: QrPosterKind }) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const [location, setLocation] = useState<PosterLocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [boards, setBoards] = useState<{ id: string; name: string }[]>([]);
  const [format, setFormat] = useState<QrPosterFormat>('a5');
  const [themeId, setThemeId] = useState<QrPosterThemeId>('lime');
  const [targetId, setTargetId] = useState('page');

  const posterRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = useState(0.5);

  // Only the people who run the place may print its poster. Same rule as the
  // profile page the button sits on — staff records carry `gym_slug` whatever
  // the location's own kind is.
  const staff = user?.staff?.find(entry => entry.gym_slug === slug) ?? null;
  const isStaffMember =
    staff?.status === 'confirmed' &&
    (staff.staff_roles.includes('admin') || staff.staff_roles.includes('editor'));

  const profilePath = `/${PROFILE_SEGMENT[kind]}/${slug ?? ''}`;
  const layout = posterLayout(format);
  const spec = layout.spec;

  useEffect(() => {
    if (!slug || !isStaffMember) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    const load = async () => {
      setLoading(true);
      try {
        if (kind === 'competition') {
          const competition = await competitionsService.getCompetitionBySlug(slug);
          const dates = [competition.start_date, competition.end_date]
            .filter(Boolean)
            .map(date => new Date(date as string).toLocaleDateString(i18n.language, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }));
          if (isMounted) {
            setLocation({
              name: competition.name,
              // When a competition runs, before where — that is what a poster
              // for it is announcing.
              subtitle: [...new Set(dates)].join(' — ') || competition.city || competition.full_address,
              logoUrl: competition.logo_url,
            });
          }
        } else {
          const place =
            kind === 'gym'
              ? await gymsService.getGymBySlug(slug)
              : await cragsService.getCragBySlug(slug);
          if (isMounted) {
            setLocation({
              name: place.name,
              subtitle: [place.city, place.country_name].filter(Boolean).join(' · ') || place.full_address,
              logoUrl: place.logo_url,
            });
          }
        }
      } catch {
        if (isMounted) setLocation(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [kind, slug, isStaffMember, i18n.language]);

  // The walls the code could point at instead of the gym's front page. Only
  // gyms have them, and only the ones this staff member can already open as a
  // tab: a poster must not send anyone to a board they cannot see.
  useEffect(() => {
    if (kind !== 'gym' || !slug || !isStaffMember) return;

    let isMounted = true;
    devicesService
      .listGymBoards(slug)
      .then(list => {
        if (isMounted) setBoards(list.map(board => ({ id: board.id, name: board.name })));
      })
      .catch(() => {
        // A gym with no boards and one whose boards could not be fetched look
        // the same here: neither is worth an error over a poster.
      });

    return () => {
      isMounted = false;
    };
  }, [kind, slug, isStaffMember]);

  // The tab strip's own URL params, so a scan lands where the poster says.
  const targets = useMemo<PosterTarget[]>(() => {
    const list: PosterTarget[] = [{ id: 'page', label: t('qrPoster.targetPage') }];
    if (kind !== 'gym') return list;

    list.push(
      { id: 'boulders', label: t('qrPoster.targetBoulders'), search: '?tab=boulders' },
      { id: 'routes', label: t('qrPoster.targetRoutes'), search: '?tab=routes' }
    );
    for (const board of boards) {
      list.push({
        id: `board:${board.id}`,
        label: board.name,
        search: `?tab=board&board=${encodeURIComponent(board.id)}`,
      });
    }
    return list;
  }, [kind, boards, t]);

  // A board that has gone away leaves its chip selected but unresolvable.
  const target = targets.find(option => option.id === targetId) ?? targets[0];

  // The sheet is drawn at its real size in millimetres; on screen it is scaled
  // down to whatever width the column happens to have.
  useEffect(() => {
    const element = previewRef.current;
    if (!element) return;

    const fit = () => {
      const available = element.clientWidth;
      if (available > 0) setPreviewScale(Math.min(1, available / (spec.pageWidthMm * PX_PER_MM)));
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, [location, spec.pageWidthMm]);

  const handlePrint = useCallback(() => {
    const node = posterRef.current;
    if (!node) return;
    trackEvent('qr_poster_printed', { kind, slug, format, theme: themeId, target: targetId });
    printNode(node, {
      // The print document's title is what "Save as PDF" fills the filename in
      // with, so it names the poster rather than the app.
      title: `divepeaks-${slug}-${format}`,
      // A5 comes two to a sheet, which only fits across a landscape A4.
      pageSize: spec.pageSize,
    });
  }, [format, kind, slug, spec.pageSize, targetId, themeId]);

  // Ctrl/Cmd+P on this page means "print the poster", not "print the app around
  // it" — which is what the browser would otherwise hand to the printer.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        handlePrint();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlePrint]);

  useBreadcrumb(
    () => ({
      onBack: () => navigate(profilePath),
      segments: [
        ...(location ? [{ label: location.name, onClick: () => navigate(profilePath) }] : []),
        { label: t('qrPoster.title'), muted: true },
      ],
      loading,
    }),
    [location?.name, profilePath, loading]
  );

  if (!slug) return <Navigate to="/not-found" replace />;

  if (!isStaffMember) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            <h1 className="text-xl font-semibold">{t('qrPoster.notAllowedTitle')}</h1>
            <p className="text-muted-foreground text-sm">{t('qrPoster.notAllowedDescription')}</p>
            <Button variant="outline" onClick={() => navigate(profilePath)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {t('qrPoster.backToPage')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="w-full max-w-[440px] aspect-[210/297] mx-auto" />
      </div>
    );
  }

  if (!location) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-muted-foreground">{t('qrPoster.failedToLoad')}</p>
            <Button variant="outline" onClick={() => navigate(profilePath)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {t('qrPoster.backToPage')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const baseUrl = `${window.location.origin}${profilePath}`;
  const url = `${baseUrl}${target.search ?? ''}`;
  // The printed line stays the bare page: a board's deep link carries a raw id
  // nobody is going to type, and the pill on the poster already says which wall
  // the code opens. Typing what is printed still lands on the right place.
  const displayUrl = baseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:items-start">
        <div className="space-y-5">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">{t('qrPoster.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('qrPoster.description', { name: location.name })}
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">{t('qrPoster.format')}</p>
            <div className="flex gap-2">
              {FORMAT_OPTIONS.map(option => {
                const cut = posterLayout(option);
                return (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={format === option}
                    onClick={() => setFormat(option)}
                    className={cn(
                      'flex-1 rounded-lg border p-3 text-left transition-colors',
                      format === option
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <span className="block text-sm font-semibold">{t(`qrPoster.format_${option}`)}</span>
                    <span className="block text-xs text-muted-foreground">
                      {t('qrPoster.copiesPerSheet', { count: copiesPerSheet(option) })}
                    </span>
                    {/* The cut size, which the margins put a little under the
                        nominal one — better said than discovered with a ruler. */}
                    <span className="mt-0.5 block text-[11px] text-muted-foreground/80">
                      {Math.round(cut.sheetWidthMm)} × {Math.round(cut.sheetHeightMm)} mm
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              {copiesPerSheet(format) > 1 ? t('qrPoster.cutHint') : t('qrPoster.fullPageHint')}
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">{t('qrPoster.theme')}</p>
            <div className="flex gap-2">
              {THEME_OPTIONS.map(option => {
                const palette = QR_POSTER_THEMES[option];
                return (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={themeId === option}
                    onClick={() => setThemeId(option)}
                    className={cn(
                      'flex-1 rounded-lg border p-3 text-left transition-colors',
                      themeId === option
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <span className="mb-2 flex gap-1" aria-hidden="true">
                      {[palette.sheet, palette.accent, palette.ink].map((swatch, index) => (
                        <span
                          key={index}
                          style={{ background: swatch }}
                          className="h-4 w-4 rounded-full border border-border"
                        />
                      ))}
                    </span>
                    <span className="block text-sm font-semibold">{t(`qrPoster.theme_${option}`)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {targets.length > 1 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t('qrPoster.target')}</p>
              <div className="flex flex-wrap gap-2">
                {targets.map(option => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={target.id === option.id}
                    onClick={() => setTargetId(option.id)}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-sm transition-colors',
                      target.id === option.id
                        ? 'border-primary bg-primary/5 font-medium'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <p className="text-sm font-medium">{t('qrPoster.linkLabel')}</p>
            <p className="text-sm text-muted-foreground break-all">{url.replace(/^https?:\/\//, '')}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={handlePrint} className="gap-2">
              <Printer className="h-4 w-4" />
              {t('qrPoster.print')}
            </Button>
            <Button variant="outline" onClick={() => navigate(profilePath)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {t('qrPoster.backToPage')}
            </Button>
          </div>
        </div>

        {/* The sheet itself, scaled to the column. Nothing here is what gets
            printed — `printNode` takes the poster's own node instead. */}
        <div className="rounded-xl border bg-muted/30 p-3 sm:p-5">
          <div ref={previewRef} className="mx-auto w-full max-w-[440px]">
            <div
              style={{ height: spec.pageHeightMm * PX_PER_MM * previewScale }}
              className="overflow-hidden"
            >
              <div
                style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left' }}
                className="shadow-lg"
              >
                <LocationQrPoster
                  ref={posterRef}
                  name={location.name}
                  subtitle={location.subtitle}
                  logoUrl={location.logoUrl}
                  url={url}
                  displayUrl={displayUrl}
                  targetLabel={target.search ? target.label : null}
                  format={format}
                  theme={themeId}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
